import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faEdit,
  faExternalLinkAlt,
  faEye,
  faLink,
  faSave,
  faSearch,
  faSpinner,
  faSyncAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../config";

const emptyEditForm = {
  tbl_id: "",
  loa_no: "",
  trans_no: "",
  physician_id: "NA",
  physician_name: "",
  specialization: "",
  amount: "",
  philhealth: "",
  member_name: "",
  member_id: "",
  provider_name: "",
  provider_code: "",
  coverage_code: "",
  trans_date: "",
  link_physician_id: "",
  link_physician_name: "",
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;
  return numericValue.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const resolveErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (!message) return fallback;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.join(", ");
  if (typeof message === "object") return Object.values(message).flat().join(", ");
  return fallback;
};

const PhysicianNotYetEnrolled = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authentication/token");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState("");

  const [selectedDetails, setSelectedDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [saving, setSaving] = useState(false);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalRecords);
    return `Showing ${from} to ${to} of ${totalRecords}`;
  }, [page, perPage, totalRecords]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/claims/processing/physicians/not-yet-enrolled`,
        {
          params: {
            page,
            per_page: perPage,
            search: search.trim(),
          },
          headers: authHeaders,
        },
      );

      setRows(response.data?.data || []);
      const pagination = response.data?.pagination || {};
      setTotalPages(pagination.total_pages || 1);
      setTotalRecords(pagination.total || 0);
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to fetch pending LOA physicians."));
      setRows([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, page, perPage, search]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const fetchDetails = async (tblId) => {
    const response = await axios.get(
      `${BASE_URL}/claims/processing/physicians/not-yet-enrolled/${encodeURIComponent(tblId)}`,
      { headers: authHeaders },
    );
    return response.data?.data || {};
  };

  const mapRowToEditForm = (row) => ({
    ...emptyEditForm,
    ...row,
    amount: row?.amount ?? "",
    philhealth: row?.philhealth ?? "",
    link_physician_name: row?.physician_name || "",
  });

  const openDetails = async (row) => {
    if (!row?.tbl_id) return;
    try {
      setDetailsLoading(true);
      const details = await fetchDetails(row.tbl_id);
      setSelectedDetails(details);
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to load LOA physician details."));
    } finally {
      setDetailsLoading(false);
    }
  };

  const openEdit = async (row) => {
    if (!row?.tbl_id) return;
    try {
      setDetailsLoading(true);
      const details = await fetchDetails(row.tbl_id);
      setEditForm(mapRowToEditForm(details.loa_physician || row));
      setEditOpen(true);
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to load pending LOA physician for editing."));
    } finally {
      setDetailsLoading(false);
    }
  };

  const updateEditField = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const savePendingUpdate = async () => {
    if (!editForm.tbl_id) return;
    if (!String(editForm.physician_name || "").trim()) {
      toast.error("Physician name is required.");
      return;
    }

    try {
      setSaving(true);
      await axios.put(
        `${BASE_URL}/claims/processing/physicians/not-yet-enrolled/${encodeURIComponent(editForm.tbl_id)}`,
        {
          physician_name: editForm.physician_name,
          specialization: editForm.specialization,
          amount: editForm.amount === "" ? null : editForm.amount,
          philhealth: editForm.philhealth === "" ? null : editForm.philhealth,
        },
        { headers: authHeaders },
      );

      toast.success("Pending LOA physician updated.");
      setEditOpen(false);
      setEditForm(emptyEditForm);
      fetchRows();
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to save pending LOA physician."));
    } finally {
      setSaving(false);
    }
  };

  const linkEnrolledPhysician = async () => {
    if (!editForm.tbl_id) return;
    if (!String(editForm.link_physician_id || "").trim()) {
      toast.error("Enrolled Physician ID is required.");
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        `${BASE_URL}/claims/processing/physicians/not-yet-enrolled/${encodeURIComponent(editForm.tbl_id)}/tag-enrolled`,
        {
          physician_id: editForm.link_physician_id,
          physician_name: editForm.link_physician_name,
        },
        { headers: authHeaders },
      );

      toast.success("LOA physician linked to enrolled physician.");
      setEditOpen(false);
      setEditForm(emptyEditForm);
      fetchRows();
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to link enrolled physician."));
    } finally {
      setSaving(false);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setPage(1);
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Physician Not Yet Enrolled</h1>
          <p className="text-sm text-gray-600">
            Review LOA physicians saved from Transaction Generator Flow Type 1 free-text entry.
          </p>
        </div>
        <button
          onClick={fetchRows}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50"
        >
          <FontAwesomeIcon icon={faSyncAlt} className="mr-2" />
          Refresh
        </button>
      </div>

      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className="text-xs text-gray-600">Search</label>
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-3 text-xs text-gray-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full rounded-lg border border-gray-200 py-2 pl-8 pr-3 text-sm"
                placeholder="LOA / TRN / physician / member / provider"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-600">Per Page</label>
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              {[10, 20, 50, 100].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button onClick={() => setPage(1)} className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white">
              Apply
            </button>
            <button onClick={resetFilters} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between p-3 text-sm text-gray-600">
          <span>{showingText}</span>
          <span>Pending LOA Physician Queue</span>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="p-3 text-left">LOA</th>
                <th className="p-3 text-left">TRN</th>
                <th className="p-3 text-left">Physician</th>
                <th className="p-3 text-left">Physician ID</th>
                <th className="p-3 text-left">Member</th>
                <th className="p-3 text-left">Provider</th>
                <th className="p-3 text-left">Coverage</th>
                <th className="p-3 text-left">Amount</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="9" className="p-5 text-center text-gray-500">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Loading pending LOA physicians...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="9" className="p-5 text-center text-gray-500">
                    No pending LOA physicians found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.tbl_id}>
                    <td className="p-3 font-medium text-gray-900">{row.loa_no || "-"}</td>
                    <td className="p-3">{row.trans_no || "-"}</td>
                    <td className="p-3">
                      <div className="max-w-xs truncate font-medium text-gray-900" title={row.physician_name || ""}>
                        {row.physician_name || "-"}
                      </div>
                      <div className="text-xs text-gray-500">{row.specialization || "-"}</div>
                    </td>
                    <td className="p-3">
                      <span className="inline-flex rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">
                        {row.physician_id || "NA"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div>{row.member_name || "-"}</div>
                      <div className="text-xs text-gray-500">{row.member_id || ""}</div>
                    </td>
                    <td className="p-3">
                      <div>{row.provider_name || row.provider_code || "-"}</div>
                      <div className="text-xs text-gray-500">{formatDateTime(row.trans_date)}</div>
                    </td>
                    <td className="p-3">{row.coverage_code || row.benefit_code || "-"}</td>
                    <td className="p-3">{formatAmount(row.amount)}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => openDetails(row)}
                          className="rounded-lg bg-slate-600 px-2 py-1 text-xs text-white hover:bg-slate-700"
                        >
                          <FontAwesomeIcon icon={faEye} className="mr-1" />
                          View
                        </button>
                        <button
                          onClick={() => openEdit(row)}
                          className="rounded-lg bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
                        >
                          <FontAwesomeIcon icon={faEdit} className="mr-1" />
                          Edit / Link
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-3 text-sm text-gray-600">
          <button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="rounded-md border border-gray-200 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page >= totalPages}
            className="rounded-md border border-gray-200 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {selectedDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Pending LOA Physician Details</h2>
                <p className="text-sm text-gray-500">
                  LOA {selectedDetails.loa_physician?.loa_no || "-"} / TRN {selectedDetails.loa_physician?.trans_no || "-"}
                </p>
              </div>
              <button onClick={() => setSelectedDetails(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {[
                ["LOA No.", selectedDetails.loa_physician?.loa_no],
                ["Transaction No.", selectedDetails.loa_physician?.trans_no],
                ["Free-text Physician", selectedDetails.loa_physician?.physician_name],
                ["Pending Physician ID", selectedDetails.loa_physician?.physician_id],
                ["Specialization", selectedDetails.loa_physician?.specialization],
                ["Amount", formatAmount(selectedDetails.loa_physician?.amount)],
                ["PhilHealth", formatAmount(selectedDetails.loa_physician?.philhealth)],
                ["Member", selectedDetails.loa_physician?.member_name || selectedDetails.loa_physician?.member_id],
                ["Provider", selectedDetails.loa_physician?.provider_name || selectedDetails.loa_physician?.provider_code],
                ["Coverage", selectedDetails.loa_physician?.coverage_code || selectedDetails.loa_physician?.benefit_code],
                ["TRN Date", formatDateTime(selectedDetails.loa_physician?.trans_date)],
                ["Pending Status", selectedDetails.loa_physician?.is_pending ? "Not Yet Enrolled" : "Already Linked"],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-gray-200 p-3">
                  <div className="text-xs uppercase text-gray-500">{label}</div>
                  <div className="mt-1 text-sm font-medium text-gray-900">{value || "-"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Edit / Link Pending LOA Physician</h2>
                <p className="text-sm text-gray-500">
                  LOA {editForm.loa_no || "-"} / TRN {editForm.trans_no || "-"}
                </p>
              </div>
              <button onClick={() => setEditOpen(false)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
              <ReadOnlyTile label="Member" value={editForm.member_name || editForm.member_id} />
              <ReadOnlyTile label="Provider" value={editForm.provider_name || editForm.provider_code} />
              <ReadOnlyTile label="Coverage" value={editForm.coverage_code} />
              <ReadOnlyTile label="TRN Date" value={formatDateTime(editForm.trans_date)} />
            </div>

            <div className="rounded-xl border border-gray-200 p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Pending LOA Physician</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <Field
                  label="Physician Name"
                  required
                  value={editForm.physician_name}
                  onChange={(value) => updateEditField("physician_name", value)}
                />
                <Field
                  label="Specialization"
                  value={editForm.specialization}
                  onChange={(value) => updateEditField("specialization", value)}
                />
                <Field
                  label="Amount"
                  type="number"
                  value={editForm.amount}
                  onChange={(value) => updateEditField("amount", value)}
                />
                <Field
                  label="PhilHealth"
                  type="number"
                  value={editForm.philhealth}
                  onChange={(value) => updateEditField("philhealth", value)}
                />
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={savePendingUpdate}
                  disabled={saving}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  {saving ? "Saving..." : "Save Free Text Update"}
                </button>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Link To Enrolled Physician</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field
                  label="Enrolled Physician ID"
                  required
                  value={editForm.link_physician_id}
                  onChange={(value) => updateEditField("link_physician_id", value)}
                />
                <Field
                  label="Physician Name"
                  value={editForm.link_physician_name}
                  onChange={(value) => updateEditField("link_physician_name", value)}
                />
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <button
                  onClick={() => navigate("/physician/add")}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" />
                  Open Physician Enrollment
                </button>
                <button
                  onClick={linkEnrolledPhysician}
                  disabled={saving}
                  className="rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={faLink} className="mr-2" />
                  {saving ? "Linking..." : "Link Enrolled Physician"}
                </button>
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <button onClick={() => setEditOpen(false)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {detailsLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/20">
          <div className="rounded-lg bg-white px-4 py-3 text-sm shadow-lg">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Loading...
          </div>
        </div>
      )}
    </div>
  );
};

const ReadOnlyTile = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
    <div className="text-xs uppercase text-gray-500">{label}</div>
    <div className="mt-1 text-sm font-medium text-gray-900">{value || "-"}</div>
  </div>
);

const Field = ({ label, value, onChange, required = false, type = "text" }) => (
  <div>
    <label className="text-xs text-gray-600">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
    />
  </div>
);

export default PhysicianNotYetEnrolled;
